import {css, StyleSheet} from 'aphrodite';
import React, {useMemo, useState} from 'react';

import * as AnalysisActions from 'analysis-tool/AnalysisActions';
import getDefaultMainViewItemForStep from 'analysis-tool/ui/getDefaultMainViewItemForStep';
import {AnalysisMainViewItemType, MainViewComponentProps} from 'analysis-tool/ui/main-view/MainViewTypes';
import {StepConfigurationViewTemplateProps} from 'analysis-tool/ui/view-templates/StepConfigurationViewTemplate';
import {assert} from 'Asserts';
import {DataFrameColumnDef} from 'data-frames/DataFrameTypes';
import CSVFilePreview from 'external-files/CSVFilePreview';
import {ExternalFlatFileDelimiter} from 'external-files/ExternalFileUtils';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {InlineBannerProps, InlineBannerType} from 'ui-library/banners/InlineBanner';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import {AsyncStatus} from 'ui-library/hooks/useAsyncState';
import * as ImportUtil from 'util/ImportUtil';

import ChooseDifferentDataFrameCreationMethodButton from '../ChooseDifferentDataFrameCreationMethodButton';
import {
  getTemplateDataFrameColumns,
  selectedColumnsToColumnMappings,
  unselectColumnsIfColumnIsNotInTemplateColumns,
  setPreviewDataFrameColumnsFromFileString,
} from '../column-mapping/ColumnMappingUtil';
import {DATA_FRAME_CREATION_NO_PREVIEW_PLACEHOLDER_TEXT} from '../DataFrameCreationConstants';
import {DataFrameNameState} from '../DataFrameCreationTypes';
import {
  getEnabledDatasetCreationMethods,
  getSharedDataFrameCreationMethodProps,
} from '../DataFrameCreationUtils';
import {DataFrameNameFieldProps} from '../steps/DataFrameNameField';
import UploadCSVStepperForm, {FileUploadState, UploadCSVFormProps} from './UploadCSVStepperForm';

const PREVIEW_DATA_FRAME_ROW_LIMIT = 1000;
const MAX_CSV_FILE_SIZE_MB = 150;

const DEFAULT_DATA_FRAME_NAME_STATE: DataFrameNameState = {name: '', hasUserEdits: false};
const DEFAULT_FILE_UPLOAD_STATE: FileUploadState = {file: null, s3URL: null, errorMessage: null};

const fileUploadGlobalState = makeUseGlobalClientState<FileUploadState>(DEFAULT_FILE_UPLOAD_STATE);
const dataFrameNameGlobalState = makeUseGlobalClientState<DataFrameNameState>(DEFAULT_DATA_FRAME_NAME_STATE);
const isFinalizingGlobalState = makeUseGlobalClientState<boolean>(false);

function buildErrorMessage(fileUPloadState: FileUploadState): Array<InlineBannerProps> {
  if (fileUPloadState.errorMessage == null) {
    return [];
  } else {
    return [{bannerType: InlineBannerType.ERROR, text: fileUPloadState.errorMessage}];
  }
}

export default function CreateDataFrameFromUploadedCsvView({
  analysis,
  clientStateId,
  onCloseStepInitializationView,
  onOpenStepInitializationView,
  selectedItem,
}: MainViewComponentProps<AnalysisMainViewItemType.UPLOAD_CSV_DATASET_CONFIGURATION>): JSX.Element {
  const [fileUploadState, setFileUploadState] = fileUploadGlobalState.useGlobalClientState(clientStateId);
  const [dataFrameNameState, setDataFrameNameState] =
    dataFrameNameGlobalState.useGlobalClientState(clientStateId);
  const [isFinalizing, setIsFinalizing] = isFinalizingGlobalState.useGlobalClientState(clientStateId);
  const templateDataFrameColumns = useMemo(
    () => getTemplateDataFrameColumns(analysis, selectedItem.stepIdToReplace),
    [analysis, selectedItem.stepIdToReplace]
  );
  const [asyncStatus, setAsyncStatus] = useState<AsyncStatus>(AsyncStatus.NOT_FETCHED);
  const [selectedColumns, setSelectedColumns] = useState<Array<DataFrameColumnDef | null>>([]);
  const [previewDataFrame, setPreviewDataFrame] = useState<{columns: Array<DataFrameColumnDef>} | null>(null);
  const [isManuallyChanged, setIsManuallyChanged] = useState<boolean>(false);

  const clearState = (): void => {
    setDataFrameNameState(DEFAULT_DATA_FRAME_NAME_STATE);
    setFileUploadState(DEFAULT_FILE_UPLOAD_STATE);
    setIsFinalizing(false);
    setPreviewDataFrameColumnsFromFileString(null, setPreviewDataFrame);
  };

  /** Returns true if file uploaded successfully */
  async function uploadFile(file: File): Promise<boolean> {
    if (file.size > MAX_CSV_FILE_SIZE_MB * 1024 * 1024) {
      setFileUploadState({
        file: null,
        s3URL: null,
        errorMessage: `File ${file.name} exceeds maximum upload size (${MAX_CSV_FILE_SIZE_MB} MB).`,
      });
      return false;
    }

    try {
      const s3URL = await ImportUtil.upload(file);
      assert(s3URL != null, 'Failed to get a valid S3 URL');

      setFileUploadState({file, s3URL, errorMessage: null});
      if (!dataFrameNameState.hasUserEdits) {
        setDataFrameNameState({name: file.name, hasUserEdits: false});
      }
      file.text().then((fileText) => {
        setPreviewDataFrameColumnsFromFileString(fileText, setPreviewDataFrame);
      });
      return true;
    } catch (error) {
      setFileUploadState({
        file: null,
        s3URL: null,
        errorMessage: `Failed to upload File ${file.name}. Reach out to support@benchling.com if the issue persists.`,
      });
      throw error;
    }
  }

  function canFinalize(): boolean {
    return (
      fileUploadState.file != null &&
      fileUploadState.s3URL != null &&
      dataFrameNameState.name.trim() !== '' &&
      // If we are replacing a step, we need to the columns from the preview loaded
      (!selectedItem.stepIdToReplace || previewDataFrame?.columns != null)
    );
  }

  async function onFinalize(): Promise<void> {
    try {
      assert(canFinalize(), 'Tried to finalize with incomplete form');

      setIsFinalizing(true);
      const newStep = await AnalysisActions.addOrReplaceAndRunAnalysisStep(
        analysis.id,
        {
          stepType: PipelineStepType.UPLOAD_CSV_DATASET,
          stepVersion: 0,
          stepConfig: {
            dataFrameName: dataFrameNameState.name,
            fileName: fileUploadState.file!.name,
            s3URL: fileUploadState.s3URL!,
          },
        },
        selectedItem.stepIdToReplace
          ? {
              originalStepId: selectedItem.stepIdToReplace,
              columnMapping: selectedColumnsToColumnMappings(selectedColumns, templateDataFrameColumns),
              allDisplayNames: previewDataFrame!.columns.map((c) => c.displayName),
            }
          : undefined
      );
      onCloseStepInitializationView(getDefaultMainViewItemForStep(newStep));
    } finally {
      clearState();
      setIsFinalizing(false);
    }
  }

  const onCancel: StepConfigurationViewTemplateProps['onCancel'] = (...args) => {
    onCloseStepInitializationView(...args);
    clearState();
  };

  const stepConfigurationViewTemplateProps: Omit<StepConfigurationViewTemplateProps, 'children'> = {
    ...getSharedDataFrameCreationMethodProps(selectedItem),
    title: 'Upload CSV',
    clientStateId,
    dataFrames: [],
    disableFinalizeButton: !canFinalize(),
    isFinalizing,
    messages: buildErrorMessage(fileUploadState),
    noPreviewPlaceholderOptions: {
      title: DATA_FRAME_CREATION_NO_PREVIEW_PLACEHOLDER_TEXT,
      subtitle: 'Upload a CSV file to see a preview of your table.',
    },
    onCancel,
    onFinalize,
    renderAdditionalPreviewContent:
      fileUploadState.file == null
        ? undefined
        : () => (
            <div className={css(styles.previewContainer)}>
              <CSVFilePreview
                url=""
                maxRows={PREVIEW_DATA_FRAME_ROW_LIMIT}
                file={fileUploadState.file ? fileUploadState.file : undefined}
                // Only allow CSV files, because the backend cannot yet parse other delimiters
                delimiter={ExternalFlatFileDelimiter.COMMA}
              />
            </div>
          ),
    toolbar: getEnabledDatasetCreationMethods().length > 1 && (
      <ChooseDifferentDataFrameCreationMethodButton
        onOpenStepInitializationView={(newSelectedItem: {
          type: AnalysisMainViewItemType.CHOOSE_DATASET_CREATION_METHOD;
        }) => {
          clearState();
          onOpenStepInitializationView({...newSelectedItem, stepIdToReplace: selectedItem.stepIdToReplace});
        }}
      />
    ),
  };

  const dataFrameNameFieldProps: DataFrameNameFieldProps = {
    ...dataFrameNameState,
    onNameChange: (newName: string) => {
      setDataFrameNameState({name: newName, hasUserEdits: true});
    },
    disabled: false,
  };

  if (previewDataFrame?.columns) {
    unselectColumnsIfColumnIsNotInTemplateColumns(
      selectedColumns,
      previewDataFrame.columns,
      setSelectedColumns
    );
  }

  const uploadCSVFormProps: UploadCSVFormProps = {
    file: fileUploadState.file,
    onRemoveFile: () => {
      setFileUploadState(DEFAULT_FILE_UPLOAD_STATE);
      if (!dataFrameNameState.hasUserEdits) {
        setDataFrameNameState({name: '', hasUserEdits: false});
      }
      setAsyncStatus(AsyncStatus.NOT_FETCHED);
      setPreviewDataFrameColumnsFromFileString(null, setPreviewDataFrame);
    },
    onSelectFile: (file: File) => {
      setAsyncStatus(AsyncStatus.LOADING);
      const successfullyUploadedPromise = uploadFile(file);
      successfullyUploadedPromise
        .then((successfullyUploaded) => {
          setAsyncStatus(successfullyUploaded ? AsyncStatus.SUCCESS : AsyncStatus.ERROR);
        })
        .catch((error) => {
          setAsyncStatus(AsyncStatus.ERROR);
          throw error;
        });
    },
  };

  return (
    <UploadCSVStepperForm
      stepConfigurationViewTemplateProps={stepConfigurationViewTemplateProps}
      dataFrameNameFieldProps={dataFrameNameFieldProps}
      fileProps={uploadCSVFormProps}
      columnMappingProps={
        selectedItem.stepIdToReplace != null
          ? {
              templateDataFrameColumns,
              newDataFrameColumns: previewDataFrame?.columns ?? null,
              newDataFrameState: asyncStatus,
              selectedColumns,
              setSelectedColumns,
              ignoreColumnValidationErrors: true,
              isManuallyChanged,
              setIsManuallyChanged,
            }
          : undefined
      }
    />
  );
}

const styles = StyleSheet.create({
  previewContainer: {
    overflowX: 'scroll',
  },
});
